import { useState } from 'react';
import { connect } from 'react-redux';
import { useExportDownload } from '@utils';
import { toast } from '../../components/utils';
import { Api } from '../../services';
import BulkDeleteScreen from './BulkDeleteScreen';

const GENERIC_ERROR = 'Bulk Deletion failed to process.';
const EMPTY_FILE_ERROR = 'The file is empty. Please use the CSV template provided.';

// Only show server text if it's a 4xx with a short, plain message.
// Anything that looks like an exception or stack trace is never shown.
const getSafeErrorMessage = (err: any): string => {
  const status = err?.response?.status;
  const message = err?.response?.data?.message;

  const looksSafe =
    typeof message === 'string' &&
    message.length > 0 &&
    message.length <= 200 &&
    !/exception|\n|\sat\s|stack|state:/i.test(message);

  if (status >= 400 && status < 500 && looksSafe) {
    return message;
  }

  return GENERIC_ERROR;
};

const onTemplate = async () => {
  const params = {
    responseType: 'blob',
    headers: { Accept: 'text/csv' },
  };

  return await Api.get('/assets/bulk-delete', params);
};

function BulkDelete({ app, onDone }) {
  const [templateData, setTemplateData] = useState<any>(null);
  const [response, setResponse] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [successItems, setSuccessItems] = useState([]);
  const [failedItems, setFailedItems] = useState([]);

  const templateName = 'BulkDeleteTemplate';

  useExportDownload(templateData, templateName);

  const csvFileSuccess = {
    headers: [
      { label: 'Name', key: 'name' },
      { label: 'Id', key: 'assetId' },
    ],
  };

  const csvFileError = {
    headers: [
      { label: 'Id', key: 'assetId' },
      { label: 'Error', key: 'msg' },
    ],
  };

  const onUpload = async (file) => {
    if (file && file.size === 0) {
      toast.error(EMPTY_FILE_ERROR);
      return;
    }

    setIsLoading(true);
    setResponse(null);

    const formData = new FormData();
    formData.append('bulkdelete.file', file);

    try {
      const { data } = await Api.post('/assets/bulk-delete', formData);

      const success = data.success || [];
      const failed = data.failure || [];

      setSuccessItems(success);
      setFailedItems(failed);
      setResponse(data);

      if (success.length > 0) {
        toast.success('Bulk Deletion processed successfully.');
      } else {
        toast.error(GENERIC_ERROR);
      }
    } catch (err: any) {
      console.error('Bulk delete failed:', err); // full details stay in the console for devs
      toast.error(getSafeErrorMessage(err));
      setResponse(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BulkDeleteScreen
      itemName="Assets"
      closeDock={onDone}
      isLoading={isLoading}
      response={response}
      successItems={successItems}
      failedItems={failedItems}
      csvFileSuccess={csvFileSuccess}
      csvFileError={csvFileError}
      onTemplate={async () => {
        const res = await onTemplate();
        setTemplateData(res.data);
      }}
      onUpload={onUpload}
    />
  );
}

const mapStateToProps = ({ app }) => ({
  app: app.app,
});

export default connect(mapStateToProps)(BulkDelete);
